import { invalidateModelCache } from "@/types/DownloadManager/modelDownloader";
import { ErrorStack } from "@/types/errors/ErrorStack";
import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { ServerEvents } from "@/types/ServerEvents";
import { AnalysesClient, CopyModelMaterialRequest, EtlJobStatus, FileResponse, Job, JobStatus, ProgressUpdate } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { nodesClient } from "@api/models";
import Connector from "@api/signalrConnection";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-lodash-debounce";

// Simple inline Service Worker cache invalidation
const invalidateServiceWorkerCache = (modelId: string) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'INVALIDATE_PROJECT_MODEL_CACHE',
      modelId: modelId
    });
  }
};

// Centralized cache clearing for model operations
const clearModelCache = (modelId: string, queryClient: any) => {
  // Remove all cached data for this model
  queryClient.removeQueries({
    predicate: (query: any) => {
      const queryKey = query.queryKey;
      return (queryKey[0] === "projectModel" && queryKey[1] === modelId) ||
        (queryKey[0] === "modelNodes" && queryKey[1] === modelId) ||
        (queryKey[0] === "categories" && queryKey[1] === modelId) ||
        (queryKey[0] === "elements" && queryKey[1] === modelId) ||
        (queryKey[0] === "components" && queryKey[1] === modelId) ||
        (queryKey[0] === "materials" && queryKey[1] === modelId);
    }
  });
};

// TODO: As a developer, I would like a cache manager that manages cache invalidation using typed keys
// TODO: As a developer, I would like a document the summarizes all of the cached data and when things are invalidated
// TODO: As a developer, I would like a mechanism to subscribe to server events in any component so that I can respond to events locally
// TODO: As a user, I would improved error reporting from background tasks
// TODO: As a user, I would like improved error reporting in the UI with full details of the error
// TODO: As a developer, I would like the production logging configuration to be adjusted for better error tracking
// TODO: As a developer, I would like the server events to be typed and documented from the server side so I know what parameters to expect
// TODO: Design Automation server events onJobComplete and onJobFailure are clearing the entire queryClient cache -- this is too aggressive
const useServerEvents = () => {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const { eventHandlers } = Connector();
  const [notifyMessage, setNotifyMessage] = useState<string>("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [openErrors, setOpenErrors] = useState(0);
  
  const debounceInvalidateEtlJobs = useDebouncedCallback(async () => {
    await appStore.queryClient.invalidateQueries({ queryKey: ["etlJobs"] });
  }, 1000);

  useEffect(() => {
    if (openErrors > 0) {
      appStore.errorManager.setOpenErrors(true);
    }
  }, [openErrors]);

  useEffect(() => {
    const serverEvents: ServerEvents = {
      // Use the Job Status cache in AppStore so we track progress 
      // for indicators added to the Projects, Project and Model pages
      onJobComplete: async (_jobId: string, job: Job) => {
        console.log("signalr onJobComplete: ", job);
        appStore.jobStatus.completeJob(job);
        // TODO: we need to clear only relevant cache, not everything
        appStore.queryClient.clear();
        setNotifyMessage(i18n.t("app.job-complete-event.message", { name: job?.model?.apsItemName }));
        setNotifyOpen(true);
        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },

      onJobFailure: async (_jobId: string, job: Job) => {
        console.log("signalr onJobFailure: ", job);

        appStore.jobStatus.failedJob(job);
        // TODO: we need to clear only relevant cache, not everything
        appStore.queryClient.clear();
        setNotifyMessage(i18n.t("app.job-failure-event.message", { name: job.model?.apsItemName }));
        setNotifyOpen(true);

        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },

      onJobStatus: async (_jobId: string, jobStatus: JobStatus) => {
        console.log("signalr onJobStatus: ", jobStatus);

        appStore.jobStatus.upsertStatus(jobStatus);

        // Show progress indicator in header (no toast notification to avoid spam)
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },

      onJobProgress: async (_jobId: string, progressUpdate: ProgressUpdate) => {
        console.log("signalr onJobProgress: ", progressUpdate);

        // Store progress in the job status store (displayed in header panel and grid)
        appStore.jobStatus.upsertProgress(progressUpdate);

        // Keep progress indicator visible during both Extract and Import phases
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
      },
      onModelImportJobStatus: async (_jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        console.log("signalr onModelImportJobStatus: ", fileName);

        setNotifyMessage(
          i18n.t("app.job-status-event.message", {
            name: fileName,
            status: i18n.t("app.progress-message.in-progress"),
          }),
        );
        setNotifyOpen(true);
        appStore.etlJobStatus.upsert(jobStatus);

        // TODO: this may interfere with the export excel progress indicator
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelImport, true);
      },
      onModelImportJobComplete: async (_jobId: string, fileName: string, jobStatus: EtlJobStatus) => {
        console.log("signalr onModelImportJobComplete: ", fileName);
        setNotifyMessage(
          i18n.t("app.job-status-event.message", {
            name: fileName,
            status: i18n.t("app.progress-message.complete"),
          }),
        );
        setNotifyOpen(true);
        // NOTE: we are coercing jobStatus to Job here since it has the same shape for our purposes
        // Text-based model import relies on UpdateModelElements which generates onJobProgress events
        // with causes the creation of a job in the jobStatus store
        appStore.jobStatus.completeJob(jobStatus as unknown as Job);
        appStore.etlJobStatus.completeJob(jobStatus);

        debounceInvalidateEtlJobs();

        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DesignAutomation, appStore.jobStatus.hasActiveJobs());
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelImport, appStore.etlJobStatus.hasActiveJobs());
      },
      onExcelExportStart: async (_projectId: string, _jobId: string) => {
        setNotifyMessage(i18n.t("app.excel-export-event.start.message"));
        setNotifyOpen(true);
      },
      onExcelExportComplete: async (projectId: string, jobId: string) => {
        try {
          const analysesClient = new AnalysesClient(undefined, axios);
          const fileContent: FileResponse = await analysesClient.getExportSelectedData(projectId, jobId);
          if (fileContent) {
            const contentType = fileContent.headers!["content-type"];
            const filename = fileContent.headers!["content-disposition"].split("filename=")[1].split(";")[0].replace(/"/g, "");
            const url = window.URL.createObjectURL(new Blob([fileContent.data], { type: contentType }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
          }
        } finally {
          appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelExport, false);
        }
      },
      onExcelExportFailure: async (_projectId: string, _jobId: string) => {
        ErrorStack.create({
          title: i18n.t("app.excel-export-event.failure.title"),
          message: i18n.t("app.excel-export-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.ExcelExport, false);
      },
      onCloneScenarioStatus: async (scenarioName: string) => {
        console.log(scenarioName);
        // Set the notification message
        const notifyMessage = `${i18n.t("app.clone-scenario-event.title", {
          name: scenarioName,
          status: i18n.t("app.progress-message.in-progress"),
        })}`;
        setNotifyMessage(notifyMessage);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, true);
      },
      onCloneScenarioComplete: async (scenarioName: string) => {
        const notifyMessage = `${i18n.t("app.clone-scenario-event.complete.message", {
          name: scenarioName,
          status: i18n.t("app.progress-message.complete"),
        })}`;
        setNotifyMessage(notifyMessage);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, false);
        appStore.setCloneScenarioId(scenarioName);
        await appStore.queryClient.resetQueries({ queryKey: ["scenarios"], exact: false });
      },

      onCloneScenarioFailure: async (scenarioName: string) => {
        console.log(scenarioName);
        ErrorStack.create({
          title: i18n.t("app.clone-scenario-event.failure.title"),
          message: i18n.t("app.clone-scenario-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CloneScenario, false);
      },

      onCopyModelMaterialStatus: async (modelName: string) => {
        setNotifyMessage(
          i18n.t("app.copy-model-material-event.title", {
            name: modelName,
            status: i18n.t("app.progress-message.in-progress"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, true);
      },
      onCopyModelMaterialComplete: async (request: CopyModelMaterialRequest) => {
        setNotifyMessage(
          i18n.t("app.copy-model-material-event.title", {
            name: request.modelName as string,
            status: i18n.t("app.progress-message.complete"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, false);
        appStore.setCopyModelMaterial(request.modelName as string);
        if (request.dstModelId && request.dstScenarioId) {
          const materialsKey = ["scenarioNodeMaterials", request.dstModelId, request.dstScenarioId] as const;
          const dataKey = ["scenarioNodeData", request.dstModelId, request.dstScenarioId] as const;
          const hasDataQuery = appStore.queryClient.getQueryState(dataKey);
          console.info("hasDataQuery", hasDataQuery);
          if (!hasDataQuery) {
            
            const scenarioNodeData = await nodesClient.getScenarioNodeData(request.dstModelId, request.dstScenarioId);
            appStore.queryClient.setQueryData(dataKey, scenarioNodeData);
            appStore.queryClient.setQueryData(materialsKey, scenarioNodeData?.nodeMaterials ?? []);
          } else {
            await appStore.queryClient.resetQueries({ queryKey: materialsKey, exact: true });
            await appStore.queryClient.resetQueries({ queryKey: dataKey, exact: true });
          }
        }
        await appStore.queryClient.resetQueries({ queryKey: ["scenarios"], exact: false });

      },

      onCopyModelMaterialFailure: async (modelName: string) => {
        ErrorStack.create({
          title: i18n.t("app.copy-model-material-event.failure.title"),
          message: modelName,
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, false);
      },

      onApsUsersSyncStatus: async () => {
        setNotifyMessage(
          i18n.t("app.aps-users-sync-event.title", {
            status: i18n.t("app.aps-users-sync-event.status.message"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, true);
      },
      onApsUsersSyncComplete: async () => {
        setNotifyMessage(
          i18n.t("app.aps-users-sync-event.title", {
            status: i18n.t("app.aps-users-sync-event.complete.message"),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },

      onApsUsersSyncFailure: async () => {
        ErrorStack.create({
          title: i18n.t("app.aps-users-sync-event.title"),
          message: i18n.t("app.aps-users-sync-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },

      onDeleteProjectComplete: async (_projectId: string, projectName: string) => {
        setNotifyMessage(
          i18n.t("app.delete-project-event.title", {
            status: i18n.t("app.delete-project-event.complete.message", { projectName }),
          }),
        );
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SyncApsUsers, false);
      },
      onDeleteProjectFailure: async (_projectId: string, projectName: string) => {
        ErrorStack.create({
          title: i18n.t("app.delete-project-event.title"),
          message: i18n.t("app.delete-project-event.failure.message", { projectName }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteProject, false);
      },
      onModelSplitStart: async (_ProjectId: string, _ModelId: string) => {
        setNotifyMessage(
          i18n.t("app.model-split-event.title", {
            status: i18n.t("app.model-split-event.start.message"),
          }),
        );

        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },
      onModelSplitComplete: async (_ProjectId: string, _modelId: string) => {
        setNotifyMessage(
          i18n.t("app.model-split-event.title", {
            status: i18n.t("app.model-split-event.complete.message"),
          }),
        );

        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },
      onModelSplitFailure: async (_ProjectId: string, _modelId: string) => {
        ErrorStack.create({
          title: i18n.t("app.model-split-event.title"),
          message: i18n.t("app.model-split-event.failure.message"),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.SplitModel, false);
      },

      onCreateVersionStatus: async (message: string) => {
        console.log("Version creation status:", message);
        setNotifyMessage(i18n.t("app.create-version-event.status.message", { message }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, true);
      },

      onCreateVersionComplete: async (response: any) => {
        setNotifyMessage(i18n.t("app.create-version-event.complete.message", {
          versionName: response.versionName || "New Version"
        }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, false);

        const currentProjectId = selectionManager.selectedProject?.projectId;
        const newModelId = response.modelId;

        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });
          await appStore.queryClient.refetchQueries({ queryKey: ["project", currentProjectId] });
        }
        if (newModelId && currentProjectId) {
          setTimeout(() => {
            selectionManager.setSelectedModel(newModelId);
          }, 500);
        }
      },

      onCreateVersionFailure: async (message: string) => {
        ErrorStack.create({
          title: i18n.t("app.create-version-event.failure.title"),
          message: i18n.t("app.create-version-event.failure.message", {
            error: message || "Failed to create model version. Please try again."
          }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CreateVersion, false);
      },

      onDeleteModelStatus: async (message: string) => {
        setNotifyMessage(i18n.t("app.delete-model-event.status.message", { message }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, true);
      },

      onDeleteModelComplete: async (response: any) => {
        setNotifyMessage(i18n.t("app.delete-model-event.complete.message", {
          modelName: response.modelName || response.ModelName || "Model"
        }));
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, false);

        // Get current project ID to invalidate the correct query
        const currentProjectId = selectionManager.selectedProject?.projectId;
        // Try multiple possible property names for model ID (same as update version)
        const deletedModelId = response.ModelId || response.modelId || response.Id || response.id || selectionManager.selectedModel?.modelId;

        // Step 1: Invalidate Service Worker cache for deleted model
        if (deletedModelId) {
          invalidateServiceWorkerCache(deletedModelId);
        }

        await invalidateModelCache(deletedModelId);
        // If the deleted model was the current model, reset selection first
        if (selectionManager.selectedModel?.modelId === deletedModelId) {
          selectionManager.setSelectedModel(null);
        }

        // Specifically invalidate and refetch project data with the correct projectId
        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });

          // Refetch project data to get updated model list
          await appStore.queryClient.refetchQueries({ queryKey: ["project", currentProjectId] });
        }

        // Remove all cached data for the deleted model
        clearModelCache(deletedModelId, appStore.queryClient);

        // Invalidate general queries to refresh any components that don't use specific model IDs
        await appStore.queryClient.invalidateQueries({ queryKey: ["projectModel"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["scenarios"] });
        await appStore.queryClient.invalidateQueries({ queryKey: ["co2MaterialAssignSettings"] });

        // After project data is refreshed, set default model if needed
        setTimeout(() => {
          if (!selectionManager.selectedModel && currentProjectId) {
            selectionManager.setDefaultModel();
          }
        }, 100); // Small delay to ensure project data is loaded
      },

      onDeleteModelFailure: async (message: string) => {
        ErrorStack.create({
          title: i18n.t("app.delete-model-event.failure.title"),
          message: i18n.t("app.delete-model-event.failure.message", {
            error: message || "Failed to delete model. Please try again."
          }),
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.DeleteModel, false);
      },

      onUpdateVersionStatus: async (message: string) => {
        setNotifyMessage(`Update: ${message}`);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, true);
      },

      onUpdateVersionComplete: async (response: any) => {
        setNotifyMessage(`Model "${response.ModelName || "Model"}" updated successfully`);
        setNotifyOpen(true);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, false);

        const currentModelId = response.ModelId || response.modelId || response.Id || response.id || selectionManager.selectedModel?.modelId;
        const currentProjectId = selectionManager.selectedProject?.projectId;
        if (currentModelId) {
          // Clear OPFS cache and metadata (model data has changed, need to re-download)
          await invalidateModelCache(currentModelId);

          await appStore.queryClient.invalidateQueries({ queryKey: ["projectModel", currentModelId] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["modelNodes", currentModelId] });
          await appStore.queryClient.invalidateQueries({ queryKey: ["materials", currentModelId] });
        }

        // Invalidate project data to refresh model list if needed
        if (currentProjectId) {
          await appStore.queryClient.invalidateQueries({ queryKey: ["project", currentProjectId] });
        }
      },

      onUpdateVersionFailure: async (response: any) => {
        ErrorStack.create({
          title: "Model Update Failed", // ← FIX: Direct text instead of missing i18n
          message: response.message || response || "Failed to update model version. Please try again.",
        });
        setOpenErrors((val) => val + 1);
        appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.UpdateVersion, false);
      },
    };

    eventHandlers(serverEvents);
  }, []);

  return { notifyMessage, notifyOpen, setNotifyOpen };
};

export default useServerEvents;